import StyledText from '../components/StyledText';
import SEO from '../components/SEO';
import { businessSchema } from '../data/schemas';
import { Link } from 'react-router-dom';

export default function Home() {
  return (
    <>
      <SEO
        title="Let's Face It Studio - Luxury Makeup Artist in Atlanta"
        description="Let's Face It Studio delivers luxury, on-location makeup artistry in Atlanta for weddings, editorials, and private events. Professional bridal makeup, editorial looks, and special event glam."
        keywords="luxury makeup artist Atlanta, bridal makeup Atlanta, wedding makeup Atlanta, editorial makeup Atlanta, Atlanta makeup artist, on-location makeup artist, professional makeup services, event makeup Atlanta"
        schema={businessSchema}
      />

      {/* Hero Section - Full Viewport */}
      <section className="relative top-0 h-[calc(100dvh-115px)] mt-0 xsm:mt-[5px] xsm2:mt-[9px] sm:mt-[23.5px] 
      sm1:mt-[18px] sm2:mt-[15px] md:mt-2 overflow-hidden md:h-[calc(100dvh-180px)] bg-[#ece6de] -mx-4 sm:-mx-[max(1rem,calc((100vw-70rem)/2))]">
        {/* Background Image */}
        <img
          src="/images/wedding_group.jpg"
          className="absolute w-full h-dvh  object-cover mt-2 sm:mt-0 md:mt-0 object-[38%_top] z-0 "
          alt="Professional makeup artist work - bridal party getting ready for wedding in Atlanta "
          loading="eager"
          fetchPriority="high"
        />

        {/* LUXURY - Top positioned */}
        <div className="absolute top-0 left-0 right-0 z-20 px-2 pt-4 sm:pt-0 md:px-4 md:pt-8 pointer-events-none">
          <div className="flex justify-center md:justify-start mt-2 xsm:mt-0 xsm2:mt-0 sm:mt-2 md:-mt-[33px]
          pointer-events-auto md:max-h-[200px] md:overflow-hidden">
            <StyledText word="LUXURY" />
          </div>
        </div>

        {/* GLAM - Bottom positioned, attached to section */}
        <div className="absolute left-0 right-0 -bottom-4 z-20 pointer-events-auto flex justify-center md:justify-end md:px-4 
        sm:bottom-[.5dvh] sm2:bottom-[1dvh] md:-bottom-[3.3dvh]">
          <StyledText word="GLAM" sizeClass="text-[clamp(110px,24vw,210px)]" />
        </div>
      </section>

      {/* Intro Section */}
      <section className="bg-[#ece6de] py-16 pt-20 md:py-24 px-4 -mx-4 sm:-mx-[max(1rem,calc((100vw-70rem)/2))]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-7xl font-serif text-slate-900 mb-13 -mt-10 tracking-wide">
            Atlanta Makeup Artistry
          </h2>
          <p className="text-lg md:text-2xl text-slate-700 leading-relaxed max-w-3xl mx-auto">
            Let's Face It Studio delivers luxury, on-location makeup artistry for weddings, editorials, and private events.
            Every brushstroke is designed to enhance your natural beauty and make you feel confident for life's most important moments.
          </p>
        </div>
      </section>

      {/* Services Preview */}
      <section className="bg-white py-16 md:py-24 px-4 -mx-4 sm:-mx-[max(1rem,calc((100vw-70rem)/2))]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-5xl md:text-6xl font-serif text-slate-900 text-center mb-12 md:-mt-12  tracking-wide">
            Services
          </h2>
          <div className="grid grid-cols-1 xsm2:grid-cols-2 md:grid-cols-3 gap-4 md:gap-8">
            {/* Bridal */}
            <Link to="/services" className="group">
              <div className="relative overflow-hidden aspect-4/3 xsm2:aspect-square bg-slate-200">
                <img
                  src="/images/examples/solo_glam.jpg"
                  alt="Bridal makeup artistry in Atlanta"
                  className="w-full h-full object-fit-bridal transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-linear-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 xsm2:p-6 text-white">
                  <h3 className="text-xl xsm2:text-2xl md:text-3xl font-serif mb-1 xsm2:mb-2">Bridal</h3>
                  <p className="text-xs xsm2:text-sm md:text-base opacity-90">Timeless elegance for your wedding day</p>
                </div>
              </div>
            </Link>

            {/* Events */}
            <Link to="/services" className="group">
              <div className="relative overflow-hidden aspect-4/3 xsm2:aspect-square bg-slate-200">
                <img
                  src="/images/examples/pink_dress.jpg"
                  alt="Event makeup services in Atlanta"
                  className="w-full h-full object-fit-events transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-linear-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 xsm2:p-6 text-white">
                  <h3 className="text-xl xsm2:text-2xl md:text-3xl font-serif mb-1 xsm2:mb-2">Events</h3>
                  <p className="text-xs xsm2:text-sm md:text-base opacity-90">Glamorous looks for any occasion</p>
                </div>
              </div>
            </Link>

            {/* Editorial */}
            <Link to="/services" className="group">
              <div className="relative overflow-hidden aspect-4/3 xsm2:aspect-square bg-slate-200">
                <img
                  src="/images/examples/hat.jpg"
                  alt="Editorial makeup for photoshoots in Atlanta"
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-linear-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 xsm2:p-6 text-white">
                  <h3 className="text-xl xsm2:text-2xl md:text-3xl font-serif mb-1 xsm2:mb-2">Editorial</h3>
                  <p className="text-xs xsm2:text-sm md:text-base opacity-90">High-fashion artistry for creative projects</p>
                </div>
              </div>
            </Link>

            {/* One-on-One Lessons */}
            <Link to="/services" className="group">
              <div className="relative overflow-hidden aspect-4/3 xsm2:aspect-square bg-slate-200 md:hidden">
                <img
                  src="/images/examples/owner_client.jpg"
                  alt="One-on-One Lessons in Atlanta"
                  className="w-full h-full object-cover object-bottom transition-transform duration-500 group-hover:scale-105"
                  style={{ objectPosition: 'center 80%' }}
                />
                <div className="absolute inset-0 bg-linear-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 xsm2:p-6 text-white">
                  <h3 className="text-xl xsm2:text-2xl md:text-3xl font-serif mb-1 xsm2:mb-2">One-on-One Lessons</h3>
                  <p className="text-xs xsm2:text-sm md:text-base opacity-90">Personal coaching tailored to your features, lifestyle, and beauty goals.</p>
                </div>
              </div>
            </Link>
          </div>

          <div className="text-center mt-16 -mb-6">
            <Link
              to="/services"
              className="inline-block border-2 border-black px-12 py-3 text-lg tracking-wide hover:bg-black hover:text-white transition-colors duration-200"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Portfolio Preview */}
      <section className="bg-[#ece6de] py-16 md:py-20 px-4 -mx-4 sm:-mx-[max(1rem,calc((100vw-70rem)/2))]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-5xl md:text-6xl font-serif text-slate-900 text-center mb-12 tracking-wide md:-mt-10 ">
            Recent Work
          </h2>
          <div className="grid grid-cols-1 xsm2:grid-cols-2 md:grid-cols-4 gap-4">
            <div className="aspect-4/3 xsm2:aspect-square overflow-hidden bg-slate-200">
              <img
                src="/images/examples/airline.jpg"
                alt="Professional makeup work - Atlanta makeup artist"
                className="w-full h-full object-cover object-bottom hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="aspect-4/3 xsm2:aspect-square overflow-hidden bg-slate-200">
              <img
                src="/images/examples/solo.jpg"
                alt="Bridal makeup portfolio - Atlanta"
                className="w-full h-full object-fit-solo hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="aspect-4/3 xsm2:aspect-square overflow-hidden bg-slate-200">
              <img
                src="/images/examples/christmas.jpg"
                alt="Event makeup services - Atlanta makeup artist"
                className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="aspect-4/3 xsm2:aspect-square overflow-hidden bg-slate-200">
              <img
                src="/images/examples/solo_grey.jpg"
                alt="Editorial makeup artistry - Atlanta"
                className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>

          <div className="text-center mt-16">
            <Link
              to="/work"
              className="inline-block border-2 border-black px-12 py-3 text-lg tracking-wide hover:bg-black hover:text-white transition-colors duration-200"
            >
              View Portfolio
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-black text-white py-20 md:py-32 px-4 -mx-4 sm:-mx-[max(1rem,calc((100vw-70rem)/2))]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-7xl font-serif mb-10 -mt-12 tracking-wide">
            Ready to Look Your Best?
          </h2>
          <p className="text-lg md:text-3xl mb-15 opacity-90">
            Book your luxury makeup experience in Atlanta today
          </p>
          <Link
            to="/book"
            className="inline-block bg-white text-black px-16 py-4 text-lg tracking-widest hover:bg-[#dfd2bf] transition-colors duration-200"
          >
            BOOK NOW
          </Link>
        </div>
      </section>
    </>
  );
}
